import { useMemo } from "react";
import { Navigate, useRouteError, isRouteErrorResponse } from "react-router-dom";
import NotFound from "@/pages/NotFound";

/**
 * Known top-level app routes. Used to salvage a real destination out of a
 * URL that an external "defense"/proxy has mangled by prepending segments
 * (e.g. `https://proxy.corp/xyz/officerhythm.com/dashboard`).
 *
 * Order matters only for readability; matching picks the earliest occurrence
 * in the mangled path regardless of list order.
 */
const KNOWN_SEGMENTS = [
  "auth/callback",
  "reset-password",
  "forgot-password",
  "verify-email",
  "dashboard",
  "analytics",
  "circles",
  "settings",
  "profile",
  "register",
  "login",
  "terms",
  "privacy",
  "acceptable-use",
] as const;

/**
 * Inspect the current (possibly proxy-mangled) URL and compute the best
 * in-app destination.
 *
 * Strategy:
 *   1. Decode the pathname (proxies often percent-encode the embedded URL).
 *   2. Find the earliest known route segment inside it.
 *   3. Rebuild a clean `/segment` path, preserving `?query` and `#hash` so
 *      auth callbacks / reset-password tokens survive the redirect.
 *   4. If nothing is recognisable, send the user to `/` — the Landing route,
 *      which forwards authenticated users onward and everyone else to login.
 */
function recoverTarget(): string {
  const { search, hash } = window.location;

  let decoded = window.location.pathname;
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    /* keep the raw pathname if it isn't valid percent-encoding */
  }
  const lower = decoded.toLowerCase();

  let bestIdx = Number.POSITIVE_INFINITY;
  let bestSeg = "";
  for (const seg of KNOWN_SEGMENTS) {
    const idx = lower.indexOf(`/${seg}`);
    if (idx !== -1 && idx < bestIdx) {
      bestIdx = idx;
      bestSeg = seg;
    }
  }

  if (bestSeg) return `/${bestSeg}${search}${hash}`;
  return "/";
}

/**
 * Catch-all element for unmatched routes. When a proxy has wrapped the app URL
 * and React Router can't match the mangled path, we redirect to the recovered
 * destination (or the main page for login) instead of showing a dead 404.
 */
export function RouteRecovery() {
  const target = useMemo(recoverTarget, []);

  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  // Defensive: never redirect to the exact same URL (would loop). If we can't
  // improve on the current location, fall back to the home/login route.
  if (target === current) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={target} replace />;
}

/**
 * Root `errorElement`. React Router routes thrown errors and 404 responses
 * here. For a genuine "no route matched" (404) we attempt URL recovery; for
 * real render/loader crashes we show the friendly NotFound screen rather than
 * risk a redirect loop back into the broken route.
 */
export function RouteErrorElement() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <RouteRecovery />;
  }
  return <NotFound />;
}
